import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Edit,
  Lock,
  Plus,
  RefreshCw,
  Unlock,
  Eye,
  Power,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  useChangeCountryStatusMutation,
  useDeleteCountryMutation,
  useLazyGetCountryByIdQuery,
} from '../../../apis/toolsApi';
import { AppModalRef } from '../../../model';
import BaseModal from '../../../ui-components/BaseModal';
import StatusTag from '../../../ui-components/StatusTag';
import ConfirmationModal from '../../../ui-components/ConfirmationModal';
import { formatTimestampToDateTime } from '../../../utils/common';
import {
  ActionMenu,
  type ActionMenuItem,
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import { API_CONSTANTS } from '../../../utils/constants';

import type { Country } from '../model';
import type { ColDef } from 'ag-grid-community';

import CountryDetailsModal from './components/CountryDetailsModal';
import CountryFormModal from './CountryFormModal';

const formatDate = (date?: string | number) => {
  return date ? formatTimestampToDateTime(date) : '-';
};

export interface CountriesPageProps {
  initialModal?: { mode: 'create' | 'edit'; countryId?: string };
  modalReturnTo?: string;
}

const Countries: React.FC<CountriesPageProps> = ({
  initialModal,
  modalReturnTo,
}) => {
  const navigate = useNavigate();
  const [changeStatus, { isLoading: isStatusLoading }] =
    useChangeCountryStatusMutation();
  const [deleteCountry] = useDeleteCountryMutation();
  const [
    fetchCountryDetails,
    { data: countryDetails, isFetching: isDetailLoading },
  ] = useLazyGetCountryByIdQuery();
  const [refreshToken, setRefreshToken] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(Boolean(initialModal));
  const [formMode, setFormMode] = useState<'create' | 'edit'>(
    initialModal?.mode ?? 'create'
  );
  const [formId, setFormId] = useState<string | undefined>(
    initialModal?.countryId
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    status: string;
    selectedIds: number[];
  } | null>(null);
  const [labels, setLabels] = useState<AppModalRef>({
    title: 'Confirm Status Change',
    content: '',
    confirmLabel: 'Yes',
    cancelLabel: 'Cancel',
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(
    null
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailCountry, setDetailCountry] = useState<Country | null>(null);

  useEffect(() => {
    if (!initialModal) return;
    setIsFormOpen(true);
    setFormMode(initialModal.mode);
    setFormId(initialModal.countryId);
  }, [initialModal]);

  const openCreateCountry = useCallback(() => {
    setFormMode('create');
    setFormId(undefined);
    setIsFormOpen(true);
  }, []);

  const openEditCountry = useCallback((countryId?: number) => {
    if (!countryId) return;
    setFormMode('edit');
    setFormId(String(countryId));
    setIsFormOpen(true);
  }, []);

  const confirmDelete = useCallback((countryId: number) => {
    setSelectedCountryId(countryId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (selectedCountryId === null) return;
    try {
      await deleteCountry(String(selectedCountryId)).unwrap();
      setIsDeleteModalOpen(false);
      setRefreshToken((prev) => prev + 1);
    } catch (error: any) {
      // Error is handled globally in baseQuery
    }
  }, [deleteCountry, selectedCountryId]);

  const handleViewDetails = useCallback(
    (country: Country) => {
      setDetailCountry(country);
      setIsDetailModalOpen(true);
      if (country.countryId) {
        fetchCountryDetails(String(country.countryId));
      }
    },
    [fetchCountryDetails]
  );

  const handleToggleStatus = useCallback(
    async (row: Country) => {
      const nextStatus = row.status === 'Active' ? 'Inactive' : 'Active';
      try {
        await changeStatus({
          ids: [row.countryId],
          status: nextStatus,
        }).unwrap();
        setRefreshToken((prev) => prev + 1);
      } catch (error: any) {
        // Error is handled globally in baseQuery
      }
    },
    [changeStatus]
  );

  const buildActions = useCallback(
    (row: Country) => {
      const actions: ActionMenuItem[] = [
        {
          label: 'View Details',
          icon: Eye,
          onClick: () => handleViewDetails(row),
        },
        {
          label: 'Edit',
          icon: Edit,
          onClick: () => openEditCountry(row.countryId),
        },
        {
          label: row.status === 'Active' ? 'Inactivate' : 'Activate',
          icon: Power,
          onClick: () => handleToggleStatus(row),
        },
        {
          label: 'Delete',
          icon: Trash2,
          tone: 'danger',
          onClick: () => confirmDelete(row.countryId),
        },
      ];
      return actions;
    },
    [handleDelete, handleViewDetails, handleToggleStatus, openEditCountry]
  );

  const NameCellRenderer = useCallback(
    (params: { data?: Country }) => {
      const row = params.data;
      if (!row) return '-';
      return (
        <button
          type="button"
          onClick={() => openEditCountry(row.countryId)}
          className="text-brand-600 hover:underline hover:text-brand-800 cursor-pointer transition font-semibold truncate"
        >
          {row.country || '-'}
        </button>
      );
    },
    [openEditCountry]
  );

  const ActionsCellRenderer = useCallback(
    (params: { data?: Country }) => {
      if (!params.data) return null;
      return (
        <ActionMenu
          items={buildActions(params.data)}
          showIcons
          buttonClassName="hover:bg-neutral-100 dark:hover:bg-neutral-800"
          iconClassName="text-neutral-600 dark:text-neutral-400"
        />
      );
    },
    [buildActions]
  );

  const countryColumns: ColDef<Country>[] = useMemo(
    () => [
      {
        headerName: 'Country Name',
        field: 'country',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 220,
        cellClass: 'min-w-0 truncate',
        cellRenderer: NameCellRenderer,
      },
      {
        headerName: 'Country Code',
        field: 'countryCode',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 140,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Country ISO Code',
        field: 'countryCodeIso3',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 140,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Dial Code',
        field: 'dialCode',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 140,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Status',
        field: 'status',
        sortable: false,
        cellRenderer: (params: any) => <StatusTag value={params.value} />,
        headerClass: 'font-bold',
        minWidth: 140,
      },
      {
        headerName: 'Action',
        field: '__actions',
        sortable: false,
        width: 80,
        headerClass: 'justify-end text-right',
        cellClass: 'justify-end text-right',
        cellRenderer: ActionsCellRenderer,
      },
    ],
    [ActionsCellRenderer, NameCellRenderer]
  );

  const handleStatusChange = useCallback(
    (selectedIds: number[], status: string) => {
      setLabels({
        title: 'Confirm Status Change',
        content: `Are you sure you want to ${status} this Country(s)?`,
        confirmLabel: 'Yes',
        cancelLabel: 'Cancel',
      });
      setModalData({ status, selectedIds });
      setIsModalOpen(true);
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    if (modalData) {
      try {
        await changeStatus({
          ids: modalData.selectedIds,
          status: modalData.status,
        }).unwrap();
        setRefreshToken((prev) => prev + 1);
      } catch (error: any) {
        // Error is handled globally in baseQuery
      } finally {
        setIsModalOpen(false);
      }
    }
  }, [modalData, changeStatus]);

  return (
    <>
      <UniversalModuleView<Country>
        moduleKey="countries"
        title="Country List"
        apiEndpoint={API_CONSTANTS.COUNTRIES_ENDPOINT}
        columnsConfig={countryColumns}
        rowKey={(row) => row.countryId}
        tableRowRenderer={undefined}
        selectable
        getRowId={(row) => row.countryId}
        refreshToken={refreshToken}
        primaryAction={{
          label: 'Add Country',
          icon: Plus,
          onClick: openCreateCountry,
        }}
        extraActions={({ reset, selectedIds }) => (
          <>
            <IconButton icon={RefreshCw} title="Refresh" onClick={reset} />
            <IconButton
              icon={Unlock}
              title="Activate"
              onClick={() => handleStatusChange(selectedIds, 'Active')}
              disabled={!selectedIds.length}
            />
            <IconButton
              icon={Lock}
              title="Inactivate"
              onClick={() => handleStatusChange(selectedIds, 'Inactive')}
              disabled={!selectedIds.length}
            />
          </>
        )}
        searchPlaceholder="Search countries by name or code..."
      />

      <CountryFormModal
        isOpen={isFormOpen}
        mode={formMode}
        countryId={formId}
        onClose={() => {
          setIsFormOpen(false);
          setFormId(undefined);
          if (modalReturnTo) {
            navigate(modalReturnTo);
          }
        }}
        onSuccess={() => setRefreshToken((prev) => prev + 1)}
      />

      {isModalOpen && (
        <BaseModal
          modalData={modalData}
          labels={labels}
          isLoading={isStatusLoading}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirm}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Country"
        message="Are you sure you want to delete this country? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        tone="danger"
      />

      <CountryDetailsModal
        isOpen={isDetailModalOpen}
        isLoading={isDetailLoading}
        countryName={detailCountry?.country || countryDetails?.data?.country}
        countryCode={
          detailCountry?.countryCode || countryDetails?.data?.countryCode
        }
        countryCodeIso3={
          detailCountry?.countryCodeIso3 ||
          countryDetails?.data?.countryCodeIso3
        }
        dialCode={detailCountry?.dialCode || countryDetails?.data?.dialCode}
        status={detailCountry?.status}
        createdDateLabel={formatDate(detailCountry?.addedDate)}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailCountry(null);
        }}
        onEdit={
          detailCountry
            ? () => {
                setIsDetailModalOpen(false);
                openEditCountry(detailCountry.countryId);
              }
            : undefined
        }
      />
    </>
  );
};

export default Countries;
